/**
 * Carries what a correct GA4 `purchase` needs across the Stripe redirect.
 *
 * Checkout leaves the SPA entirely (`window.location.href = data.url`) and
 * comes back to `/?checkout=success`, so nothing in React state survives the
 * round trip. `sessionStorage` does — same tab, same origin — which is exactly
 * the scope we want: it expires with the tab, so a stale entry can never
 * attach itself to somebody else's later purchase.
 *
 * The amount is Stripe's own `amount_total`, handed back by `create-checkout`.
 * It is never a constant in this repo: the account's previous yearly price was
 * $50, not $10, and a hardcoded value would have reported the wrong revenue
 * for as long as nobody checked.
 *
 * Known gap, accepted: a buyer who closes the tab on Stripe's page — or pays
 * on a different device — never returns here, so no `purchase` fires. Closing
 * that needs the webhook reporting server-side via the Measurement Protocol.
 * Stripe remains the source of truth for revenue either way.
 */

const PENDING_PURCHASE_KEY = "analytics_pending_purchase";

export interface PendingPurchase {
  sessionId?: string;
  /** Stripe's minor units (cents), as returned by the Checkout session. */
  amountTotal?: number | null;
  currency?: string | null;
  /** Where checkout was clicked (`getUpgradeEntry`), for the browser's `purchase`. */
  upgradeEntry?: string | null;
}

export function stashPendingPurchase(purchase: PendingPurchase): void {
  try {
    sessionStorage.setItem(PENDING_PURCHASE_KEY, JSON.stringify(purchase));
  } catch {
    // Private mode or storage disabled. The purchase event still fires on
    // return, just without the transaction id and amount.
  }
}

/**
 * Read and clear in one step — a purchase must be reported at most once, and
 * leaving the entry behind would let a back-navigation double-count it.
 */
export function takePendingPurchase(): PendingPurchase | null {
  try {
    const raw = sessionStorage.getItem(PENDING_PURCHASE_KEY);
    sessionStorage.removeItem(PENDING_PURCHASE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingPurchase;
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

/** Stripe reports minor units; GA4 wants major. Undefined when unknown. */
export function toMajorUnits(amountTotal?: number | null): number | undefined {
  if (typeof amountTotal !== "number" || !Number.isFinite(amountTotal)) return undefined;
  return amountTotal / 100;
}
